import { Component, OnInit, Inject, PLATFORM_ID, OnDestroy, ElementRef, AfterViewInit, ViewChild } from '@angular/core';
import { isPlatformBrowser } from "@angular/common";

import { AuthService } from "../services/auth.service";
import { HeightWidthService, IHeightWidth  } from "../common/services/height-width.service";
import { MainScrollService } from "../common/services/main-scroll.service";
import { LayoutService } from "./services/layout.service";
import { Subscription } from "rxjs";
import { EventsService } from "../services/events.service";
import { SidePanel } from "../common/models/side-panel.model";
import { StateType } from "../common/models/flow.dto";
import { SidePanelService } from "../common/services/side-panel.service";
import { ApplicationConfigService, ModuleConfig } from "../services/application-config.service";

@Component({
    selector: 'scroll-content',
    templateUrl: './scroll-content.component.html',
    providers: [HeightWidthService, MainScrollService],
    standalone: false
})

export class ScrollContentComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('main', { static: true }) main: ElementRef;
    @ViewChild('scrollHead', { static: true }) scrollHead: ElementRef;

    private _moduleConfiguration: ModuleConfig;

    blackoutStage: boolean = false;

    public _layoutSubscription: Subscription;
    public _userSubscription: Subscription;
    public _configSubscription: Subscription;
    public layout: string = 'homeComponent';
    public _firstHWCalc: boolean = true;
    public count: number = 0;
    public mediumBP: number = 640;     //These need to match _settings.scss $breakpoints
    public largeBP: number = 1024;
    public elWorkflowBuilderComponent;



    public notesSidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Field Notes", stateTypeRequired: StateType.ReviewForm }, { title: "Review Notes", stateTypeRequired: StateType.ReviewForm }, { title: "Timeline", stateTypeRequired: StateType.ReviewForm }],
        sections: [{ componentName: 'FieldNotes' }, { componentName: 'ReviewNotes' }, { componentName: 'History' }]
    });
    // Inject Auth Service on app start
    constructor(
        public readonly authService: AuthService,
        @Inject(PLATFORM_ID) public readonly platformId: Object,
        public layoutService: LayoutService,
        public heightWidthService: HeightWidthService,
        public el: ElementRef,
        public _eventsService: EventsService,
        public sidePanelService: SidePanelService,
        public mainScrollService: MainScrollService,
        public applicationConfigService: ApplicationConfigService
    ) {
    }
    // OnInit - prepare app and oauth service
    ngOnInit(): void {

        this._configSubscription = this.applicationConfigService.getModuleConfig().pipe()
            .subscribe(
				(conf: ModuleConfig) => {
                    //this doesn't really need to do anything; calling it alone should cache the necessary config in the service
                },
                (error) => { console.error(error); });


        this._layoutSubscription = this.layoutService.getLayout().subscribe(layout => {
            this.layout = layout.component;
        });
        this._userSubscription = this.authService.loggedInUser$.subscribe(user => {
            if (user && user.isLoggedIn) {
                this.blackoutStage = !user.canLogin;
            } else {
                this.blackoutStage = false;
            }
        }, (error) => {
            console.log(error);
            this.blackoutStage = false;
        });;

        //create subscription and call checknav on window resize
        this._eventsService.create('resize', window, 'resize', () => this.onResize());

        //only way I could get a scroll event to fire properly
        window.addEventListener('scroll', this.scroll, {passive: true, capture: true});      

    }

    ngAfterViewInit(): void {
        this.elWorkflowBuilderComponent= this.el.nativeElement.querySelector('.workflowBuilderComponent');

        //emit init inital size
        this.onResize();

        //send scroll event
        this.scroll();
    }

    ngOnDestroy(): void {
        if (this._layoutSubscription)
            this._layoutSubscription.unsubscribe();
        if (this._configSubscription) this._configSubscription.unsubscribe();
        if (this._userSubscription) this._userSubscription.unsubscribe();
        this._eventsService.remove('resize');
        window.removeEventListener('scroll', this.scroll, true);
    }

    /**
     * Get scroll offset position and scrollTop
     */
    scroll = (): void => {

        let scrollTop = this.scrollHead.nativeElement.scrollTop;
        let offset = this.offset(this.scrollHead.nativeElement);
        //send scroll info through service
        this.mainScrollService.sendMainScrollInfo({
            offset: offset,
            scrollTop: scrollTop
        });
    };

    /**
     * Get position relative to the document
     * @param el
     */
    offset(el): any {
        var rect = el.getBoundingClientRect(),
            scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
            scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        return { top: rect.top + scrollTop, left: rect.left + scrollLeft }
    }

    /**
     * window resize
     */
    onResize() {

        if (this._firstHWCalc) {
            //wait for digest  (first time only)
            setTimeout(() => {
                this.sendHW();
                this._firstHWCalc = false;
            }, 1);
        } else {
            this.sendHW();
        }
    }

    /**
    * Sends h/w information on window resize (for diagram h/w calc's)
    */
    sendHW(): void {

        //get main element (full height of scrolling area)
        let elMain = <HTMLDivElement>this.main.nativeElement;
        //get scrolling native element
        let elScrollHeadNatEl = this.scrollHead.nativeElement;
        let elMainClientRec = elMain.getBoundingClientRect();

        //get element with padding around diagram

        if (elMain != undefined) {
            let diagramPadding = 0;

            if (this.elWorkflowBuilderComponent != undefined) {
                //using paddingTop for FireFox & IE bug
                diagramPadding = parseInt(getComputedStyle(this.elWorkflowBuilderComponent).paddingTop);
            }

            let hw: IHeightWidth = {
                mainHeight: elMain.clientHeight,
                mainWidth: elMain.clientWidth,
                diagramPadding: diagramPadding,
                scrollHeadHeight: elScrollHeadNatEl.clientHeight,
                scrollHeadWidth: elScrollHeadNatEl.clientWidth,
                scrollHeadMarginTop: parseInt(getComputedStyle(elScrollHeadNatEl).marginTop),
                scrollOffsetTop: elMainClientRec.top,
                scrollOffsetBottom: elMainClientRec.bottom,
                windowHeight: window.innerHeight,
                windowWidth: window.innerWidth,
                isBelowMediumBP: (window.matchMedia("(max-width: " + this.mediumBP + "px)") as any).matches,
                isBelowLargeBP: (window.matchMedia("(max-width: " + this.largeBP + "px)") as any).matches
            };
            this.heightWidthService.sendHW(hw);
        }
    }
    /**
     * Skip button click
     */
    skipToMain(): void {

        let mainNatEl = this.main.nativeElement;

        //timeout required to set focus
        setTimeout(() => {
            mainNatEl.focus();
        }, 1);

    }
}
